import React, { lazy, Suspense, useCallback, useState } from 'react';
import {
  type ActionEntry,
  type BasePassage,
  type ReadingPlanDay,
  type SpecialActionTypeIds,
  getFormattedRef,
} from '@soulhx/fs-common';
import { DateTime } from 'luxon';
import { Form, ListGroup } from 'react-bootstrap';
import { PlanItemStats } from '../helpers/PlanItemStats.js';
import { getOtNtPassageStatsFromOsis, getReadingPlanClass } from '../helpers/helper-functions.js';
import { useTranslation } from 'react-i18next';
import { StyledButton } from '../helpers/read/StyledButton.js';
import { type SetMessageFunction } from '../../../hooks/ErrorsAndWarnings.js';
import { clsx, SHXCol, SHXRow } from '@soulhx/ui-common';
import {
  type ActiveInstantiatedPlan,
  localToday,
  queueLocalActionEntryCreate,
  queueLocalCatchUp,
  queueLocalIpDayUpdate,
  queueLocalPassageCreate,
} from '@soulhx/client-store';
import { PassageLink } from '@soulhx/bible-viewer';
import { SubscribedPlanItem } from '../SubscribedPlanItem.js';
const OnlineBibleViewer = lazy(() => import('./OnlineBibleViewer.js'));

interface IReadingPlanItem {
  plan: ReadingPlanDay;
  /**
   * The subscription `plan` is from; can be missing briefly while a
   * subscription is mid-sync
   */
  activePlan?: ActiveInstantiatedPlan;
  specialTypeIds: SpecialActionTypeIds;
  specialTypesLoading: boolean;
  dateToShow: string;
  version: string;
  addErrorMessage: SetMessageFunction;
  addInfoMessage: SetMessageFunction;
  showUnsub?: boolean;
  addCompletedAe: (newAE: ActionEntry) => void;
}
export const ReadingPlanItem = ({
  plan,
  activePlan,
  specialTypeIds,
  specialTypesLoading,
  dateToShow,
  version,
  addErrorMessage,
  addInfoMessage,
  addCompletedAe,
  showUnsub = false,
}: IReadingPlanItem) => {
  const [showPreview, setShowPreview] = useState<boolean>(false);
  // set on click so the item shows its new state straight away, rather than
  // waiting for the live query behind `plan` to re-run; dropped once `plan`
  // catches up (or if saving fails)
  const [pendingCompleted, setPendingCompleted] = useState<boolean | undefined>(undefined);
  if (pendingCompleted !== undefined && plan.dayCompleted === pendingCompleted) {
    setPendingCompleted(undefined);
  }
  const dayCompleted = pendingCompleted ?? plan.dayCompleted;
  const [showCatchupOptions, setShowCatchupOptions] = useState<boolean>(false);
  const [markPrevCatchupRead, setMarkPrevCatchupRead] = useState<boolean>(true);
  const { t } = useTranslation(['plan']);

  // every completed reading gets its own action entry: users can have any
  // number of entries of a type on a day (e.g. short OT readings from two
  // plans). Dated now, since it records when the reading was actually done,
  // whichever day's reading is being viewed.
  const markAEComplete = useCallback(
    async (actionTypeId: string, displayName: string) => {
      const entryDate = DateTime.now().toISO()!;
      const newID = await queueLocalActionEntryCreate(actionTypeId, true, entryDate);

      addCompletedAe({ id: newID, actionType: actionTypeId, completed: true, entryDate, displayName });
    },
    [addCompletedAe]
  );

  const passage: BasePassage = { version: version, osis: plan.osisForDay || '' };

  const { otVerses, ntVerses, formattedRef } = getOtNtPassageStatsFromOsis(plan.osisForDay);
  const otConditionMet: boolean = otVerses > 10;
  const ntConditionMet: boolean = ntVerses > 10;

  const className = getReadingPlanClass({ ...plan, dayCompleted });

  return (
    <ListGroup.Item
      variant={clsx({
        success: className === 'rpi-completed',
        danger: className === 'rpi-red',
        warning: className === 'rpi-yellow',
      })}
    >
      <>
        <SHXRow>
          <SHXCol xs={12} md={dayCompleted && !showUnsub ? 12 : 6}>
            <strong>{plan.planName}:</strong>
            <PassageLink passage={passage.osis} version={version} includeVerses={false}>
              {formattedRef}
            </PassageLink>
          </SHXCol>
          {(!dayCompleted || showUnsub) && (
            <SHXCol xs={12} md={6}>
              <PlanItemStats
                otVerses={otVerses}
                ntVerses={ntVerses}
                otConditionMet={otConditionMet}
                ntConditionMet={ntConditionMet}
                rpVariant={className}
              />
            </SHXCol>
          )}
        </SHXRow>
        {showUnsub && activePlan && (
          <SHXRow>
            <SHXCol>
              <SubscribedPlanItem
                subscription={activePlan}
                today={localToday()}
                compact
                variant={
                  className === 'rpi-completed'
                    ? 'success'
                    : className === 'rpi-red'
                      ? 'danger'
                      : className === 'rpi-yellow'
                        ? 'warning'
                        : undefined
                }
              />
            </SHXCol>
          </SHXRow>
        )}

        <SHXRow className="m-0 p-0">
          <SHXCol className="m-0 p-0">
            <StyledButton
              rpVariant={className}
              type="complete"
              itemType={t('plan:itemType')}
              done={dayCompleted}
              disabled={specialTypesLoading}
              onClick={async (e) => {
                setShowPreview(false);
                e.currentTarget.blur();

                if (specialTypesLoading) return;
                const completed = !dayCompleted;
                setPendingCompleted(completed);

                await queueLocalIpDayUpdate(plan.ipDayId, { completed })
                  .then(async () => {
                    if (!completed) return;

                    if (ntConditionMet) {
                      await markAEComplete(specialTypeIds.longNT, t('do:actionTypes.long.longNT'));
                    }
                    if (otConditionMet) {
                      await markAEComplete(specialTypeIds.longOT, t('do:actionTypes.long.longOT'));
                    }
                    if (ntVerses > 0 && !ntConditionMet) {
                      await markAEComplete(specialTypeIds.shortNT, t('do:actionTypes.long.shortNT'));
                    }
                    if (otVerses > 0 && !otConditionMet) {
                      await markAEComplete(specialTypeIds.shortOT, t('do:actionTypes.long.shortOT'));
                    }
                  })
                  .catch(() => {
                    setPendingCompleted(undefined);
                    addErrorMessage(t('plan:item.errorMarkingComplete'));
                  });
              }}
            />
          </SHXCol>

          <SHXCol className="m-0 p-0">
            <StyledButton
              rpVariant={className}
              type="save"
              done={false}
              tooltip={t('plan:item.pinItemTooltip')}
              onClick={() => {
                queueLocalPassageCreate(passage.osis, passage.version);
                addInfoMessage(t('plan:item.saved'));
              }}
            />
          </SHXCol>

          <SHXCol className="m-0 p-0">
            <StyledButton
              rpVariant={className}
              type="calendar"
              tooltip={t('plan:item.resetItemTooltip')}
              onClick={() => setShowCatchupOptions((prev) => !prev)}
            />
          </SHXCol>

          <SHXCol className="m-0 p-0">
            <StyledButton
              rpVariant={className}
              type="openCloseBible"
              done={showPreview}
              onClick={() => setShowPreview((prev) => !prev)}
              disabled={!plan.osisForDay}
              tooltip={t('plan:item.showViewerTooltip')}
            />
          </SHXCol>
        </SHXRow>

        {showCatchupOptions && (
          <SHXRow className="m-0 p-0">
            <SHXCol>
              <Form.Text>{t('plan:item.resetItemHelpText')}</Form.Text>

              <Form.Check
                type="switch"
                checked={markPrevCatchupRead}
                onChange={() => setMarkPrevCatchupRead((prev) => !prev)}
                label={t('plan:item.markPrevDaysReadLabel')}
              />

              <StyledButton
                rpVariant={className}
                type="save"
                tooltip={t('plan:item.resetItemTooltip')}
                onClick={() => {
                  queueLocalCatchUp(plan.ipId, dateToShow, markPrevCatchupRead).catch(() =>
                    addErrorMessage(t('plan:item.errorCatchingUp'))
                  );
                }}
              />
            </SHXCol>
          </SHXRow>
        )}

        {showPreview && plan.osisForDay && (
          <SHXRow className="m-0 p-0">
            <SHXCol className="m-0 p-0">
              <Suspense fallback=". . .">
                <OnlineBibleViewer
                  passage={getFormattedRef(plan.osisForDay!, { includeVerses: false })}
                  version={version}
                />
              </Suspense>
            </SHXCol>
          </SHXRow>
        )}
      </>
    </ListGroup.Item>
  );
};
